import { CSSProperties, FC } from 'react';

import { Checkbox, CheckboxProps, alpha, useTheme } from '@mui/material';

import { useModuleChipColors } from '@/shared/lib/hooks/useModuleChipColors';

export type CheckboxSize = 'small' | 'medium' | 'large';

const CHECKBOX_SIZES: Record<CheckboxSize, { container: number; icon: number; padding: number }> = {
  small: { container: 16, icon: 12, padding: 2 },
  medium: { container: 18, icon: 14, padding: 2 },
  large: { container: 20, icon: 16, padding: 2 },
};

const checkboxContainerStyle = (color: string, size: CheckboxSize): CSSProperties => {
  const { container, padding } = CHECKBOX_SIZES[size];
  return {
    display: 'flex',
    width: `${container}px`,
    height: `${container}px`,
    padding: `${padding}px`,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '4px',
    background: color,
    flexShrink: 0,
  };
};

const UncheckedIcon: FC<{ disabled?: boolean; size: CheckboxSize }> = ({ disabled, size }) => {
  const { container } = CHECKBOX_SIZES[size];
  return (
    <span
      style={{
        display: 'block',
        width: `${container}px`,
        height: `${container}px`,
        borderRadius: '4px',
        background: disabled ? 'rgba(103, 110, 118, 0.1)' : '#FEFEFE',
        flexShrink: 0,
      }}
    />
  );
};

const CheckedIcon: FC<{
  color: string;
  size: CheckboxSize;
  disabled?: boolean;
  disabledActiveBg: string;
  disabledIconColor: string;
}> = ({ color, size, disabled, disabledActiveBg, disabledIconColor }) => {
  const { icon } = CHECKBOX_SIZES[size];
  return (
    <span style={checkboxContainerStyle(disabled ? disabledActiveBg : color, size)}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={icon}
        height={icon}
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M3 8.5L7 12.5L13 3.5"
          stroke={disabled ? disabledIconColor : '#FEFEFE'}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
};

const IndeterminateIcon: FC<{
  color: string;
  size: CheckboxSize;
  disabled?: boolean;
  disabledActiveBg: string;
  disabledIconColor: string;
}> = ({ color, size, disabled, disabledActiveBg, disabledIconColor }) => {
  const { icon } = CHECKBOX_SIZES[size];
  return (
    <span style={checkboxContainerStyle(disabled ? disabledActiveBg : color, size)}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={icon}
        height={icon}
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M15.0013 10.8327H5.0013C4.54297 10.8327 4.16797 10.4577 4.16797 9.99935C4.16797 9.54102 4.54297 9.16602 5.0013 9.16602H15.0013C15.4596 9.16602 15.8346 9.54102 15.8346 9.99935C15.8346 10.4577 15.4596 10.8327 15.0013 10.8327Z"
          fill={disabled ? disabledIconColor : '#FEFEFE'}
        />
      </svg>
    </span>
  );
};

const UNCHECKED_SHADOW =
  '0 3px 5px 0 rgba(103, 110, 118, 0.08), 0 0 0 2px rgba(103, 110, 118, 0.16), 0 1px 1px 0 rgba(0, 0, 0, 0.12)';

interface CustomCheckboxProps extends CheckboxProps {
  checkboxSize?: CheckboxSize;
}

const CustomCheckbox: FC<CustomCheckboxProps> = ({ checkboxSize, ...props }) => {
  const { text: moduleColor } = useModuleChipColors();
  const { palette } = useTheme();
  const { disabled, size } = props;

  const resolvedSize: CheckboxSize = checkboxSize ?? (size === 'small' ? 'small' : 'medium');

  const disabledActiveBg = palette.grey[200];
  const disabledIconColor = palette.grey[500];

  const checkedShadow = `0 2px 5px 0 ${alpha(moduleColor, 0.28)}, 0 0 0 1px ${alpha(moduleColor, 0.16)}, 0 1px 1px 0 ${alpha(moduleColor, 0.12)}`;

  return (
    <Checkbox
      {...props}
      checked={props.checked ?? false}
      icon={<UncheckedIcon disabled={disabled} size={resolvedSize} />}
      checkedIcon={
        <CheckedIcon
          color={moduleColor}
          size={resolvedSize}
          disabled={disabled}
          disabledActiveBg={disabledActiveBg}
          disabledIconColor={disabledIconColor}
        />
      }
      indeterminateIcon={
        <IndeterminateIcon
          color={moduleColor}
          size={resolvedSize}
          disabled={disabled}
          disabledActiveBg={disabledActiveBg}
          disabledIconColor={disabledIconColor}
        />
      }
      sx={{
        padding: 0,
        borderRadius: '4px',
        '&:not(.Mui-checked):not(.MuiCheckbox-indeterminate)': {
          boxShadow: UNCHECKED_SHADOW,
        },
        '&.Mui-checked, &.MuiCheckbox-indeterminate': {
          boxShadow: checkedShadow,
        },
        '&.Mui-disabled.Mui-checked, &.Mui-disabled.MuiCheckbox-indeterminate': {
          boxShadow: `0 2px 5px 0 ${alpha(palette.grey[400], 0.28)}, 0 0 0 1px ${alpha(palette.grey[400], 0.16)}, 0 1px 1px 0 ${alpha(palette.grey[400], 0.12)}`,
        },
        ...props.sx,
      }}
    />
  );
};

export default CustomCheckbox;
